<script setup lang="ts">
import { ExpertReviewSchema, UiButton, UiCard, UiChip, UiIcon, UiText, UiAccordion, UiDivider, UiTooltip } from '@shopexperts/shared';
import { z } from 'zod';
import { computed } from 'vue';
import ReviewerInfo from './shared/ReviewerInfo.vue';
import RatingDisplay from './shared/RatingDisplay.vue';
import ReviewMetadata from './shared/ReviewMetadata.vue';

type Review = z.infer<typeof ExpertReviewSchema>;

const props = withDefaults(defineProps<{
    review: Review;
    canReply?: boolean;
    replyLockedTooltip?: string;
}>(), {
    canReply: true,
    replyLockedTooltip: undefined,
});

const emit = defineEmits<{
    respond: [reviewId: number];
    share: [reviewId: number];
}>();

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

const handleRespond = () => {
    emit('respond', props.review.id);
};

const editResponse = () => {
    emit('respond', props.review.id);
};

const ratings = computed(() => [
    { label: 'Quality of Work', value: props.review.quality },
    { label: 'Communication', value: props.review.communication },
    { label: 'Turnaround Timelines', value: props.review.timelines },
    { label: 'Value for Money', value: props.review.value_for_money },
]);
</script>

<template>
    <UiCard padding="md">
        <div class="flex flex-col md:flex-row items-start md:justify-between justify-start gap-6 md:gap-6">
            <!-- Left side: Main content -->
            <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-4 md:mb-4">
                    <ReviewerInfo :name="props.review.client_name" :avatar-url="props.review.client_avatar_url"
                        :company-name="props.review.client_company"
                        :company-website="props.review.client_company_website"
                        :is-repeat-client="props.review.client_is_repeat_client"
                        :is-hired-on-shopexperts="props.review.is_hired_on_shopexperts"
                        :posted-at="formatDate(props.review.created_at)" />

                </div>

                <ReviewMetadata class="hidden md:block" :project-value="props.review.project_value_range"
                    :review-source="props.review.review_source"
                    :apps-and-themes="props.review.apps_and_themes as any" />
            </div>

            <div class="flex w-full flex-col gap-3 md:w-auto md:items-end">
                <div v-if="review.status === 'published'" class="hidden md:block">
                    <UiButton label="Share Review" variant="outline" size="sm" @click="emit('share', review.id)" />
                </div>
                <RatingDisplay label="Overall Rating" :ratings="ratings" :rating="props.review.overall_rating"
                    mobile-label="Rating: " />
            </div>
        </div>
        <UiText as="p" variant="p" class="text-gray-700 leading-relaxed md:mb-4 mt-4 md:mt-4"
            :text="props.review.review_text" />
        <ReviewMetadata class="block md:hidden" :project-value="props.review.project_value_range"
            :review-source="props.review.review_source" :apps-and-themes="props.review.apps_and_themes as any" />
        <UiDivider />
        <UiAccordion v-if="props.review.review_response">
            <template #trigger>
                <UiText as="span" variant="p-small" class="font-semibold text-gray-900" text="Your Response" />
            </template>
            <div class="pt-3 flex flex-col gap-3">
                <UiText as="p" variant="p" class="text-gray-700 leading-relaxed bg-gray-50 p-3 rounded"
                    :text="props.review.review_response.response_text" />
                <button v-if="props.canReply" @click="editResponse"
                    class="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 hover:underline cursor-pointer">
                    <UiIcon name="pencil" class="w-4 h-4" />
                    <UiText as="span" variant="p-small" text="Edit Your Response" />
                </button>
            </div>
        </UiAccordion>
        <div v-else-if="props.canReply">
            <button @click="handleRespond"
                class="inline-flex items-center gap-2 text-sm font-semibold text-gray-900 hover:text-gray-700 hover:underline cursor-pointer">
                <UiIcon name="pencil" class="w-4 h-4" />
                <UiText as="span" variant="p-small" text="Write Your Response" />
            </button>
        </div>
        <div v-else>
            <UiTooltip :content="props.replyLockedTooltip ?? ''" :disabled="!props.replyLockedTooltip">
                <span class="inline-flex items-center gap-2" data-testid="write-response-locked">
                    <button type="button" disabled aria-disabled="true"
                        class="inline-flex items-center gap-2 text-sm font-semibold text-gray-400 cursor-not-allowed">
                        <UiIcon name="pencil" class="w-4 h-4" />
                        <UiText as="span" variant="p-small" text="Write Your Response" />
                    </button>
                    <UiChip v-if="props.replyLockedTooltip" text="PRO" size="xs" rounded="small" variant="dark" class="font-semibold" />
                </span>
            </UiTooltip>
        </div>
        <UiButton v-if="review.status === 'published'" label="Share Review" variant="outline" size="sm"
            class="mt-4 w-full md:hidden" @click="emit('share', review.id)" />
    </UiCard>
</template>
